import { useAppDispatch, useAppSelector } from "app/hooks";
import Button from "shared/components/Button";
import StatIcon from "shared/components/StatIcon";
import { defineFieldKPIs } from "shared/components/StatIcon/statIconModels";
import { Tooltip } from "react-tooltip";
import { setOpenPanel } from "features/settings/details-slice";
import { fieldReportNewWindow } from "shared/helpers/sharedOnClickFunctions";
import { ReactComponent as InfoSVG } from "assets/icons/info.svg";
import { ReactComponent as FieldReportSVG } from "assets/icons/field-report.svg";
import { ReactComponent as SettingsSVG } from "assets/icons/settings.svg";
import SkippyIconCircle from "assets/skippy-icon-circle.png";
import { STORAGE_URL } from "config";
import { DELAY_SHOW } from "shared/helpers/reactTooltip";
import Transition, { TransitionProps } from "react-transition-group/Transition";
import { useRef } from "react";
import useMedia from "use-media";
import { MOBILE_VIEW_BREAKPOINT } from "shared/helpers/mediaQueries";
import PortalledContent from "shared/components/PortalledContent";

// NOTE: These Tooltips are portalled due to "Small screen height fix"
// See _PageSideMenu.scss for acompanying css

/**
 * `$transitionShort` out
 * `$transitionShort` in
 *
 * Same as PageBottomMenu
 * Linked to PageBottomMenu
 */
const TIMEOUTS: TransitionProps["timeout"] = {
  enter: 0,
  appear: 300,
  exit: 300,
};

const KPIs = defineFieldKPIs([
  // Overall good health
  "health",
  "cropcover",
  "crop_uniformity",
  // Good health (other)
  "gai",
  "count",
  "count_per_area",
  "ear_count",
  "ear_count_per_area",
  "growth_stage_aab",
  "growth_stage_bbch",
  // Overual bad health
  "unhealthy",
  "weeds",
  "insect_damage",
  // Bad health (other)
  "flowering",
  "ripening",
  "desaturated",
  "yellow_leaves",
  "purple_leaves",
  //
] as const);

interface PageSideMenuProps {
  menusInView: boolean;
}

const PageSideMenu: React.FC<PageSideMenuProps> = ({ menusInView }) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const largerThanMobileView = useMedia(MOBILE_VIEW_BREAKPOINT);
  const showMenus = useAppSelector((state) => state.settings.showMenus);
  const guid = useAppSelector((state) => state.panorama.guid);
  const currentFieldDetails = useAppSelector(
    (state) => state.panorama.currentFieldDetails
  );
  const imperialUnits = useAppSelector((state) => state.panorama.imperialUnits);

  const dispatch = useAppDispatch();
  return (
    <Transition
      nodeRef={nodeRef}
      in={menusInView && showMenus && largerThanMobileView}
      timeout={TIMEOUTS}
      mountOnEnter
      // Don't unmount as this is a key component of the UI
      // unmountOnExit
    >
      {(transitionState) => (
        <aside ref={nodeRef} className={`PageSideMenu ${transitionState}`}>
          {/* Icon */}
          <img
            src={`${STORAGE_URL}/Panos/${guid}/Logo.png`}
            // Possible solution using client_logo
            // src={`${STORAGE_URL}/Panos/${guid}/${
            //   currentFieldDetails?.client_logo || "Logo.png"
            // }`}
            // Fallback icon if the image fails to load
            onError={(e) => (e.currentTarget.src = SkippyIconCircle)}
            height={48}
            width={48}
            // Empty alt text to prevent screen readers from reading the image
            // (i.e it's a decorative image)
            alt=""
          />
          {/* Farm Details menu (button) */}
          <div data-tooltip-id="MAIN_MENU_FARM_DETAILS">
            <Button
              colour="Theme"
              additionalClassNames="PrimaryButton NoBorder"
              onClick={() => {
                dispatch(setOpenPanel("farmDetails"));
              }}
              children={<InfoSVG height={20} width={20} />}
            />
            <PortalledContent
              children={
                <Tooltip
                  id="MAIN_MENU_FARM_DETAILS"
                  place="right"
                  className="ToolTip"
                  delayShow={DELAY_SHOW}
                  children="Farm details"
                />
              }
            />
          </div>
          {/* Farm report button */}
          <div data-tooltip-id="MAIN_MENU_FIELD_REPORT">
            <Button
              colour="Theme"
              additionalClassNames="PrimaryButton NoBorder"
              disabled={!currentFieldDetails?.field_report_url || !guid}
              onClick={fieldReportNewWindow(currentFieldDetails, guid)}
              // onClick={fieldReportInRightPanel(dispatch)}
              children={<FieldReportSVG height={20} width={20} />}
            />
            <PortalledContent
              children={
                <Tooltip
                  id="MAIN_MENU_FIELD_REPORT"
                  place="right"
                  className="ToolTip"
                  delayShow={DELAY_SHOW}
                  children="Field Report"
                />
              }
            />
          </div>
          {/* Spacer */}
          <hr />
          {/* KPI items (scrollable) */}
          <div className="DisplayFlex FlexColumn MainMenuStats">
            {currentFieldDetails &&
              KPIs.map((kpi, index) => (
                <StatIcon
                  key={`KPI_${index}`}
                  targetObject="field"
                  targetKey={kpi}
                  // NOTE: As `any` is required here until we fix the
                  // `unknown` types in the API
                  value={currentFieldDetails[kpi] as any}
                  imperialUnits={imperialUnits}
                  iconSize={28}
                  parentClassNames="StatIcon"
                  valueClassNames="StatContent"
                  viewType="mini"
                  tooltipTarget="mainSidePanel"
                  // tooltipContentKey="name"
                />
              ))}
            <h5 className="ImportantSubText NoDetailsMessage">
              None Available
            </h5>
          </div>
          {/* Spacer */}
          <hr />
          {/* Settings menu (button) */}
          <div data-tooltip-id="MAIN_MENU_SETTINGS">
            <Button
              colour="Theme"
              additionalClassNames="PrimaryButton NoBorder"
              onClick={() => {
                dispatch(setOpenPanel("settings"));
              }}
              children={<SettingsSVG height={20} width={20} />}
            />
            <PortalledContent
              children={
                <Tooltip
                  id="MAIN_MENU_SETTINGS"
                  place="right"
                  className="ToolTip"
                  delayShow={DELAY_SHOW}
                  children="Settings"
                />
              }
            />
          </div>
        </aside>
      )}
    </Transition>
  );
};

export default PageSideMenu;
